import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from "@mui/icons-material/History";
import BoltIcon from "@mui/icons-material/Bolt";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import TuneIcon from "@mui/icons-material/Tune";
import TranslateIcon from "@mui/icons-material/Translate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { useTour } from "../context/TourContext";
import Btn from "./Btn";

const btnBase = {
  padding: "3px 8px",
  borderRadius: 5,
  border: "1px solid rgba(255,255,255,0.3)",
  cursor: "pointer",
  fontSize: "0.72rem",
  background: "transparent",
  color: "inherit",
};
const btnActive = { ...btnBase, background: "rgba(255,255,255,0.2)", fontWeight: 700 };

const LANGUAGES = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

const INTENSITIES = ["low", "medium", "high"];

export default function Header() {
  const { t, i18n } = useTranslation();
  const { state: user, setState: setUser } = useContext(AuthContext);
  const isConnected = !!user;
  const muiTheme = useTheme();
  const { mode, intensity, changeMode, changeIntensity, changeLanguage } = useThemeMode();
  const navigate = useNavigate();
  const { startTour } = useTour();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [intensityOpen, setIntensityOpen] = useState(false);

  const handleLogout = () => {
    fetch("/api/logout", { method: "POST", credentials: "include" }).then(() => {
      setUser(null);
      navigate("/");
    });
  };

  const drawerBg = muiTheme.custom?.myheader?.main ?? "#0f172a";
  const drawerText = muiTheme.custom?.myheader?.text ?? "#e2e8f0";

  const DrawerContent = () => (
    <Box
      sx={{
        width: 270,
        height: "100%",
        backgroundColor: drawerBg,
        color: drawerText,
        display: "flex",
        flexDirection: "column",
        py: 1,
      }}
      role="presentation"
    >
      {/* En-tête du drawer */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
        <img src="/favicon.svg" alt="logo" style={{ width: 28, height: 28 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: drawerText }}>
          Inner Levels Insight
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      <List dense sx={{ flex: 1 }}>
        {/* Navigation */}
        {isConnected && (
          <ListItemButton
            onClick={() => { navigate("/gauges"); setDrawerOpen(false); }}
            sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
              <BoltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("header.navGauges")} primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}
        {isConnected && (
          <ListItemButton
            onClick={() => { navigate("/history"); setDrawerOpen(false); }}
            sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("header.navHistory")} primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 0.5 }} />

        {/* Langue */}
        <ListItemButton
          onClick={() => setLangOpen((v) => !v)}
          sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
        >
          <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
            <TranslateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={LANGUAGES.find((l) => l.code === i18n.resolvedLanguage)?.flag ?? "🌐"}
            secondary={LANGUAGES.find((l) => l.code === i18n.resolvedLanguage)?.label}
            primaryTypographyProps={{ fontSize: "1rem" }}
            secondaryTypographyProps={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)" }}
          />
          {langOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
        <Collapse in={langOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {LANGUAGES.map((lng) => (
              <ListItemButton
                key={lng.code}
                onClick={() => { changeLanguage(lng.code); setLangOpen(false); }}
                sx={{
                  pl: 4,
                  color: i18n.resolvedLanguage === lng.code ? "#93c5fd" : drawerText,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 30, fontSize: "1rem" }}>
                  {lng.flag}
                </ListItemIcon>
                <ListItemText primary={lng.label} primaryTypographyProps={{ fontSize: "0.85rem" }} />
                {i18n.resolvedLanguage === lng.code && <CheckIcon fontSize="small" sx={{ color: "#93c5fd" }} />}
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Dark / Light (toggle direct) */}
        <ListItemButton
          onClick={() => changeMode(mode === "dark" ? "light" : "dark")}
          sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
        >
          <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
            {mode === "dark" ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={t(`header.${mode}`)}
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </ListItemButton>

        {/* Intensité du thème */}
        <ListItemButton
          onClick={() => setIntensityOpen((v) => !v)}
          sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
        >
          <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t(`header.${intensity}`)}
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
          {intensityOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
        <Collapse in={intensityOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {INTENSITIES.map((v) => (
              <ListItemButton
                key={v}
                onClick={() => { changeIntensity(v); setIntensityOpen(false); }}
                sx={{
                  pl: 4,
                  color: intensity === v ? "#93c5fd" : drawerText,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                }}
              >
                <ListItemText primary={t(`header.${v}`)} primaryTypographyProps={{ fontSize: "0.85rem" }} />
                {intensity === v && <CheckIcon fontSize="small" sx={{ color: "#93c5fd" }} />}
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 0.5 }} />

        {/* Aide */}
        {isConnected && (
          <ListItemButton
            onClick={() => { startTour(); setDrawerOpen(false); }}
            sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
              <HelpOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("header.guidedTour")} primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}

        {/* Paramètres */}
        {isConnected && (
          <ListItemButton
            onClick={() => { navigate("/settings"); setDrawerOpen(false); }}
            sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: drawerText, minWidth: 36 }}>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Paramètres" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      {/* Déconnexion / Connexion */}
      <List dense>
        {isConnected ? (
          <ListItemButton
            onClick={() => { handleLogout(); setDrawerOpen(false); }}
            sx={{ color: "#f87171", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: "#f87171", minWidth: 36 }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Déconnexion" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        ) : (
          <ListItemButton
            onClick={() => { navigate("/connexion"); setDrawerOpen(false); }}
            sx={{ color: drawerText, "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemText primary={t("header.loginSignup")} primaryTypographyProps={{ fontSize: "0.85rem" }} />
          </ListItemButton>
        )}
      </List>
    </Box>
  );

  /* ── VERSION MOBILE ── */
  if (isMobile) {
    return (
      <>
        <AppBar position="sticky" elevation={0} sx={{ backgroundColor: muiTheme.custom.myheader.main }}>
          <Toolbar sx={{ justifyContent: "space-between", minHeight: "48px !important", px: 1.5 }}>
            {/* Logo — ouvre le drawer */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              size="small"
              sx={{ p: 0.5 }}
              aria-label="menu"
            >
              <img src="/favicon.svg" alt="logo" style={{ width: 32, height: 32 }} />
            </IconButton>

            {/* Titre centré */}
            <Typography
              variant="subtitle1"
              sx={{
                color: muiTheme.custom.myheader.text,
                fontWeight: "bold",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              Inner Levels Insight
            </Typography>

            {/* Espace droit vide (équilibre visuel) */}
            <Box sx={{ width: 40 }} />
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { backgroundColor: drawerBg } }}
        >
          <DrawerContent />
        </Drawer>
      </>
    );
  }

  /* ── VERSION DESKTOP ── */
  return (
    <AppBar position="sticky" elevation={0} sx={{ backgroundColor: muiTheme.custom.myheader.main }}>
      <Toolbar sx={{ justifyContent: "space-between", gap: 1, flexWrap: "wrap", minHeight: "48px !important" }}>

        {/* Titre + Navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Btn
            path="/"
            sx={{ width: "auto", flex: "none" }}
            msg={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1 }}>
                <img src="/favicon.svg" alt="Inner Levels Insight logo" style={{ width: 32, height: 32 }} />
                <Typography variant="h6" sx={{ color: muiTheme.custom.myheader.text, fontWeight: "bold", whiteSpace: "nowrap" }}>
                  Inner Levels Insight
                </Typography>
              </Box>
            }
          />
          {isConnected && (
            <Btn
              path="/gauges"
              sx={{ width: "auto", flex: "none" }}
              msg={
                <Typography variant="body2" sx={{ color: muiTheme.custom.myheader.text, px: 1, whiteSpace: "nowrap" }}>
                  {t("header.navGauges")}
                </Typography>
              }
            />
          )}
          {isConnected && (
            <Btn
              path="/history"
              sx={{ width: "auto", flex: "none" }}
              msg={
                <Typography variant="body2" sx={{ color: muiTheme.custom.myheader.text, px: 1, whiteSpace: "nowrap" }}>
                  {t("header.navHistory")}
                </Typography>
              }
            />
          )}
        </Box>

        {/* Droite : langue + thème + actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/* Sélecteur de langue */}
          <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
            {["fr", "en"].map((lng) => (
              <button
                key={lng}
                onClick={() => changeLanguage(lng)}
                style={i18n.resolvedLanguage === lng ? btnActive : btnBase}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </Box>

          <span style={{ opacity: 0.3, fontSize: "0.7rem" }}>|</span>

          {/* Contrôles thème */}
          <Box sx={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" }}>
            {["dark", "light"].map((m) => (
              <button key={m} onClick={() => changeMode(m)} style={mode === m ? btnActive : btnBase}>
                {t(`header.${m}`)}
              </button>
            ))}
            <span style={{ opacity: 0.3, fontSize: "0.7rem" }}>|</span>
            {[["low", "low"], ["medium", "medium"], ["high", "high"]].map(([v, k]) => (
              <button key={v} onClick={() => changeIntensity(v)} style={intensity === v ? btnActive : btnBase}>
                {t(`header.${k}`)}
              </button>
            ))}
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isConnected && (
              <IconButton onClick={startTour} size="small" title={t("header.guidedTour")}>
                <HelpOutlineIcon sx={{ color: muiTheme.custom.myheader.text }} />
              </IconButton>
            )}
            {isConnected && (
              <IconButton onClick={() => navigate("/settings")} size="small">
                <SettingsIcon sx={{ color: muiTheme.custom.myheader.text }} />
              </IconButton>
            )}
            {isConnected ? (
              <IconButton onClick={handleLogout} size="small">
                <LogoutIcon sx={{ color: muiTheme.custom.myheader.text }} />
              </IconButton>
            ) : (
              <button onClick={() => navigate("/connexion")} style={{ ...btnBase, lineHeight: 1.2, textAlign: "center" }}>
                {t("header.loginSignup")}
              </button>
            )}
          </Box>
        </Box>

      </Toolbar>
    </AppBar>
  );
}
